import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Monitor, MapPin, Clock, PlayCircle, Film, LogIn, Ban, RotateCcw, CreditCard, RefreshCw, KeyRound, Copy, Euro } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDur } from './SubscriberStats';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fdatetime = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fdate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function RefundModal({ row, light, onClose }) {
  const [charges, setCharges] = useState(null);
  const [sel, setSel] = useState(null);
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const tx = light ? 'text-gray-900' : 'text-white';
  const th = light ? 'text-gray-500' : 'text-white/40';
  const inputCls = `w-full rounded-lg border px-3 py-2 text-xs outline-none ${light ? 'bg-gray-50 border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`;

  useEffect(() => {
    axios.get(`${API}/admin/subscribers/${encodeURIComponent(row.email)}/charges`, { withCredentials: true })
      .then(r => {
        const list = r.data.charges || [];
        setCharges(list);
        const first = list.find(c => c.remaining > 0);
        if (first) { setSel(first.charge_id); setAmount(String(first.remaining.toFixed(2))); }
      })
      .catch(e => { toast.error(e.response?.data?.detail || 'Chargement des paiements impossible'); setCharges([]); });
  }, [row.email]);

  const selected = (charges || []).find(c => c.charge_id === sel);
  const isTotal = selected && Math.abs(parseFloat(amount || 0) - selected.remaining) < 0.005;

  const doRefund = async () => {
    if (!selected) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || amt > selected.remaining + 0.001) { toast.error(`Montant invalide (max ${selected.remaining.toFixed(2)} €)`); return; }
    if (!window.confirm(`Rembourser ${amt.toFixed(2)} € à ${row.name || row.email} ?\n\nLe remboursement sera effectué immédiatement sur Stripe et un email de confirmation sera envoyé à l'abonné.\n\nL'abonnement n'est PAS résilié (action distincte).`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/subscribers/${encodeURIComponent(row.email)}/refund`,
        { charge_id: sel, amount: amt, custom_message: message.trim() }, { withCredentials: true });
      toast.success(`Remboursement ${data.type} de ${data.amount.toFixed(2)} € effectué — email envoyé à l'abonné`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec du remboursement'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="refund-modal">
      <div className={`w-full max-w-md rounded-2xl border p-5 max-h-[90vh] overflow-y-auto ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`}>
        <div className="flex items-center justify-between mb-1">
          <h3 className={`text-sm font-bold flex items-center gap-2 ${tx}`}><Euro size={14} className="text-[#E9641A]" /> Rembourser {row.name || row.email}</h3>
          <button onClick={onClose} className={th} data-testid="refund-close"><X size={16} /></button>
        </div>
        <p className={`text-[11px] mb-3 ${th}`}>Remboursement Stripe immédiat + email automatique à l'abonné. N'affecte pas l'abonnement.</p>
        {charges === null ? (
          <p className={`text-xs py-6 text-center ${th}`}>Chargement des paiements…</p>
        ) : charges.length === 0 ? (
          <p className={`text-xs py-6 text-center ${th}`}>Aucun paiement trouvé pour cet abonné.</p>
        ) : (
          <>
            <p className={`text-[11px] font-bold mb-1.5 ${th}`}>PAIEMENT À REMBOURSER</p>
            <div className="space-y-1.5 mb-3 max-h-44 overflow-y-auto">
              {charges.map(c => (
                <label key={c.charge_id} data-testid={`refund-charge-${c.charge_id}`}
                  className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-xs cursor-pointer ${sel === c.charge_id ? 'border-[#E9641A] bg-[#E9641A]/10' : light ? 'border-gray-200' : 'border-white/10'} ${c.remaining <= 0 ? 'opacity-45' : ''}`}>
                  <input type="radio" name="charge" disabled={c.remaining <= 0} checked={sel === c.charge_id}
                    onChange={() => { setSel(c.charge_id); setAmount(String(c.remaining.toFixed(2))); }} className="accent-[#E9641A]" />
                  <span className={`flex-1 ${tx}`}>
                    <b>{c.amount.toFixed(2)} €</b> · {fdate(c.date)}
                    {c.refunded_amount > 0 && <span className="text-amber-400"> · déjà remboursé : {c.refunded_amount.toFixed(2)} €</span>}
                    {c.remaining <= 0 && <span className="text-red-400"> · intégralement remboursé</span>}
                  </span>
                </label>
              ))}
            </div>
            {selected && (
              <>
                <p className={`text-[11px] font-bold mb-1.5 ${th}`}>MONTANT (max {selected.remaining.toFixed(2)} €) — {isTotal ? 'remboursement TOTAL' : 'remboursement PARTIEL'}</p>
                <div className="flex items-center gap-2 mb-3">
                  <input type="number" min="0.01" step="0.01" max={selected.remaining} value={amount}
                    onChange={e => setAmount(e.target.value)} className={inputCls} data-testid="refund-amount" />
                  <button onClick={() => setAmount(String(selected.remaining.toFixed(2)))} data-testid="refund-full-btn"
                    className={`flex-none px-3 py-2 rounded-lg text-[11px] font-semibold border ${light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>Total</button>
                </div>
                <p className={`text-[11px] font-bold mb-1.5 ${th}`}>MESSAGE PERSONNALISÉ DANS L'EMAIL (optionnel)</p>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} data-testid="refund-message"
                  placeholder="Ex : Suite à votre signalement, nous vous remercions pour votre patience et votre coopération…"
                  className={`${inputCls} resize-none mb-4`} />
                <button onClick={doRefund} disabled={busy} data-testid="refund-submit"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold bg-[#E9641A] hover:bg-[#f0763a] text-white transition-colors disabled:opacity-40">
                  <Euro size={13} /> {busy ? 'Remboursement en cours…' : `Rembourser ${parseFloat(amount || 0).toFixed(2)} €`}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const SUB_STATUS = {
  active: ['Actif', 'bg-green-500/15 text-green-500'],
  trialing: ['Essai', 'bg-blue-500/15 text-blue-400'],
  past_due: ['Impayé', 'bg-amber-500/15 text-amber-400'],
  canceled: ['Résilié', 'bg-red-500/15 text-red-400'],
  incomplete: ['Incomplet', 'bg-gray-500/15 text-gray-400'],
  incomplete_expired: ['Expiré', 'bg-gray-500/15 text-gray-400'],
  unpaid: ['Impayé', 'bg-amber-500/15 text-amber-400'],
};

function SubscriptionHistory({ email, light }) {
  const [histo, setHisto] = useState(null);
  useEffect(() => {
    setHisto(null);
    axios.get(`${API}/admin/subscribers/subscription-history`, { params: { email }, withCredentials: true })
      .then(r => setHisto(r.data)).catch(() => setHisto({ subscriptions: [], count: 0 }));
  }, [email]);
  const th = light ? 'text-gray-500' : 'text-white/40';
  const tx = light ? 'text-gray-900' : 'text-white';
  return (
    <div className="mb-5" data-testid="drawer-sub-history">
      <p className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${th}`}>
        <CreditCard size={11} className="inline mr-1" />Historique des abonnements{histo ? ` (${histo.count})` : ''}
        {histo?.resubscribed && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#75D4D5]/15 text-[#75D4D5] normal-case tracking-normal"><RefreshCw size={9} className="inline mr-0.5" />Réabonné</span>}
      </p>
      {!histo && <p className={`text-xs ${th}`}>Chargement de l'historique Stripe…</p>}
      {histo && histo.count === 0 && <p className={`text-xs ${th}`}>Aucun abonnement Stripe trouvé.</p>}
      {histo && histo.subscriptions.map((s, i) => {
        const [lbl, cls] = SUB_STATUS[s.status] || [s.status, 'bg-gray-500/15 text-gray-400'];
        const ongoing = ['active', 'trialing', 'past_due'].includes(s.status);
        const end = s.ended_at || s.canceled_at;
        return (
          <div key={i} className={`rounded-lg border p-2.5 mb-1.5 ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/10'}`} data-testid={`sub-history-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <p className={`text-xs font-semibold ${tx}`}>
                <span className={`inline-block w-4 h-4 rounded-full text-[9px] font-bold text-center leading-4 mr-1.5 ${light ? 'bg-gray-200 text-gray-600' : 'bg-white/10 text-white/60'}`}>{i + 1}</span>
                {s.plan_label || 'Offre inconnue'} · {(s.amount / 100).toFixed(2)} €/{s.interval === 'year' ? 'an' : 'mois'}
              </p>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${cls}`}>{lbl}</span>
            </div>
            <p className={`text-[10px] mt-1 ml-6 ${th}`}>
              {ongoing
                ? <>Depuis le <b className={tx}>{fdate(s.started_at)}</b>{s.cancel_at_period_end && s.current_period_end ? <> · résiliation programmée, accès jusqu'au <b className="text-red-400">{fdate(s.current_period_end)}</b></> : <> · <b className="text-green-500">en cours</b>{s.current_period_end ? ` · renouvellement le ${fdate(s.current_period_end)}` : ''}</>}</>
                : <>Du <b className={tx}>{fdate(s.started_at)}</b> au <b className={tx}>{fdate(end || s.current_period_end)}</b></>}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function LastSessionModal({ email, light, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    axios.get(`${API}/admin/activity/last-session`, { params: { email }, withCredentials: true })
      .then(r => setD(r.data)).catch(() => setErr('Impossible de charger la dernière session.'));
  }, [email]);
  const tx = light ? 'text-gray-900' : 'text-white';
  const th = light ? 'text-gray-500' : 'text-white/40';
  const box = light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/10';
  const s = d?.session;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="last-session-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} data-testid="last-session-backdrop" />
      <div className={`relative w-[440px] max-w-full max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl p-5 ${light ? 'bg-white' : 'bg-[#1a0e2e] border border-white/10'}`}>
        <div className="flex items-start justify-between mb-3">
          <h3 className={`text-sm font-bold ${tx}`}><LogIn size={13} className="inline mr-1.5 text-[#75D4D5]" />Dernière connexion</h3>
          <button onClick={onClose} className={`p-1 rounded-lg ${light ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-white/10 text-white/60'}`} data-testid="last-session-close"><X size={15} /></button>
        </div>
        {err && <p className={`text-xs ${th}`}>{err}</p>}
        {!err && !d && <p className={`text-xs py-6 text-center ${th}`}>Chargement…</p>}
        {d && !s && <p className={`text-xs ${th}`}>Dernière connexion : <b className={tx}>{fdatetime(d.last_login_at)}</b><br />Aucune session d'activité détaillée enregistrée (données conservées 30 jours).</p>}
        {s && (
          <>
            <div className={`rounded-lg border p-3 mb-3 ${box}`} data-testid="last-session-info">
              <p className={`text-xs font-semibold ${tx}`}>{fdatetime(s.started_at)} → {new Date(s.last_seen).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                <span className="ml-2 text-[#E9641A] font-bold">{fmtDur(s.duration_s)}</span></p>
              <p className={`text-[11px] mt-1.5 ${th}`}>
                <Monitor size={10} className="inline mr-1" />{s.device || 'Appareil inconnu'}
                {s.city && <> · <MapPin size={10} className="inline mx-0.5" />{s.city}</>}
                {s.profile_name && <> · Profil : <b className={tx}>{s.profile_name}</b></>}
              </p>
              <p className={`text-[11px] mt-0.5 ${th}`}>{s.nav_count || 0} navigation(s) · {fmtDur(s.watch_seconds)} de lecture</p>
            </div>
            {s.viewed_pages.length > 0 && (
              <div className="mb-3">
                <p className={`text-[11px] font-bold uppercase tracking-wide mb-1.5 ${th}`}>Fiches consultées</p>
                <div className="flex flex-wrap gap-1.5">{s.viewed_pages.map(p => <span key={p} className={`px-2 py-1 rounded-full text-[10px] font-medium ${light ? 'bg-gray-100 text-gray-600' : 'bg-white/5 text-white/60'}`}>{p}</span>)}</div>
              </div>
            )}
            <p className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${th}`}><PlayCircle size={11} className="inline mr-1" />Visionnages de cette session ({d.watch.length})</p>
            {d.watch.length === 0 && <p className={`text-xs ${th}`}>{s.watched.length > 0 ? `Titres lancés : ${s.watched.join(', ')}` : 'Aucun contenu visionné pendant cette session.'}</p>}
            <div className="space-y-2" data-testid="last-session-watch-list">
              {d.watch.map((r, i) => (
                <div key={`${r.content_id}-${i}`} className={`rounded-lg border p-2.5 flex gap-2.5 ${box}`}>
                  {r.thumbnail_url && <img src={r.thumbnail_url} alt="" className="w-9 object-cover rounded flex-none" style={{ height: 52 }} />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${tx}`}>{r.title}</p>
                    <p className={`text-[10px] capitalize ${th}`}>{r.type}{r.profile_name ? ` · ${r.profile_name}` : ''} · {fdatetime(r.updated_at)}</p>
                    {typeof r.progress === 'number' && (
                      <div className="flex items-center gap-1 mt-1">
                        <div className={`h-1 flex-1 rounded-full overflow-hidden ${light ? 'bg-gray-200' : 'bg-white/10'}`}>
                          <div className="h-full bg-[#E9641A] rounded-full" style={{ width: `${Math.min(100, r.progress)}%` }} />
                        </div>
                        <span className={`text-[9px] ${th}`}>{Math.round(r.progress)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function SubscriberDrawer({ row, onClose, theme, onCanceled }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [accessMode, setAccessMode] = useState(null);
  const [accessBusy, setAccessBusy] = useState(false);

  useEffect(() => {
    if (!row.has_account) return;
    axios.get(`${API}/admin/subscribers/${encodeURIComponent(row.email)}/access`, { withCredentials: true })
      .then(r => setAccessMode(r.data.mode)).catch(() => setAccessMode('auto'));
  }, [row.email, row.has_account]);

  const changeAccess = async (mode) => {
    if (mode === 'blocked' && !window.confirm(`Bloquer l'accès Maya+ de ${row.name || row.email} ?\nIl ne pourra plus rien regarder, même s'il est abonné.`)) return;
    if (mode === 'granted' && !window.confirm(`Activer l'accès Maya+ GRATUIT pour ${row.name || row.email}, même sans abonnement ?`)) return;
    setAccessBusy(true);
    try {
      await axios.post(`${API}/admin/subscribers/${encodeURIComponent(row.email)}/access`, { mode }, { withCredentials: true });
      setAccessMode(mode);
      toast.success(mode === 'blocked' ? 'Accès bloqué' : mode === 'granted' ? 'Accès activé (offert)' : 'Accès repassé en automatique (abonnement)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec'); }
    setAccessBusy(false);
  };

  const deleteAccount = async () => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT le compte de ${row.name || row.email} ?\n\nCompte, accès, historique et liste seront effacés. Action irréversible.`)) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${encodeURIComponent(row.email)}`, { withCredentials: true });
      toast.success('Compte supprimé définitivement');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Suppression impossible'); }
  };
  const [tempPass, setTempPass] = useState('');
  const [resetting, setResetting] = useState(false);
  const [showLastSession, setShowLastSession] = useState(false);

  const resetPassword = async () => {
    if (resetting) return;
    if (!window.confirm(`Réinitialiser le mot de passe de ${row.email} ?\n\nUn mot de passe temporaire sera généré : communiquez-le à l'abonné, qui pourra ensuite le changer depuis son compte.`)) return;
    setResetting(true);
    try {
      const { data: d } = await axios.post(`${API}/admin/users/${encodeURIComponent(row.email)}/reset-password`, {}, { withCredentials: true });
      setTempPass(d.password || '');
      toast.success('Mot de passe réinitialisé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
    setResetting(false);
  };

  const cancelSub = async () => {
    if (canceling) return;
    if (!window.confirm(`Résilier l'abonnement de ${row.name || row.email} ?\n\nL'abonné conservera l'accès jusqu'à la fin de sa période en cours, la résiliation sera effectuée sur Stripe et un email de confirmation lui sera envoyé.`)) return;
    setCanceling(true);
    try {
      await axios.post(`${API}/admin/subscribers/${encodeURIComponent(row.email)}/cancel`, { customer_id: row.customer_id || '' }, { withCredentials: true });
      toast.success('Résiliation prise en compte — email de confirmation envoyé');
      onCanceled?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la résiliation'); }
    setCanceling(false);
  };
  const resumeSub = async () => {
    if (canceling) return;
    if (!window.confirm(`Réactiver l'abonnement de ${row.name || row.email} ?\n\nLa résiliation programmée sera annulée sur Stripe et l'abonnement continuera normalement.`)) return;
    setCanceling(true);
    try {
      await axios.post(`${API}/admin/subscribers/${encodeURIComponent(row.email)}/resume`, { customer_id: row.customer_id || '' }, { withCredentials: true });
      toast.success('Abonnement réactivé — la résiliation est annulée');
      onCanceled?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la réactivation'); }
    setCanceling(false);
  };
  const canCancel = ['active', 'trialing', 'past_due'].includes(row.sub_status) && !row.cancel_at_period_end;

  useEffect(() => {
    if (!row?.email) { setError('Email inconnu — impossible de charger l\'activité.'); return; }
    axios.get(`${API}/admin/subscribers/user-activity`, { params: { email: row.email }, withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.status === 404
        ? 'Aucun compte Maya+ associé à cet email — pas de données de visionnage (abonné Stripe uniquement).'
        : 'Erreur de chargement.'));
  }, [row]);

  const tx = light ? 'text-gray-900' : 'text-white';
  const th = light ? 'text-gray-500' : 'text-white/40';
  const chip = light ? 'bg-gray-100 text-gray-600' : 'bg-white/5 text-white/60';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="subscriber-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} data-testid="drawer-backdrop" />
      <div className={`relative w-[480px] max-w-[95vw] h-full overflow-y-auto shadow-2xl animate-slideInRight ${light ? 'bg-white' : 'bg-[#150a26] border-l border-white/10'}`}>
        <div className={`sticky top-0 z-10 px-5 py-4 flex items-start justify-between border-b ${light ? 'bg-white border-gray-200' : 'bg-[#150a26] border-white/10'}`}>
          <div className="min-w-0">
            <h2 className={`text-base font-bold truncate ${tx}`} data-testid="drawer-name">{row.name || row.email || '—'}</h2>
            <p className={`text-xs truncate ${th}`}>{row.email}{row.phone ? ` · ${row.phone}` : ''}</p>
            <p className={`text-[11px] mt-0.5 ${th}`}>{row.plan_label || 'Sans offre'} · Total payé : <span className="text-[#E9641A] font-bold">{((row.total_paid || 0) / 100).toFixed(2)} €</span></p>
            <button onClick={() => setShowLastSession(true)} className={`block text-[11px] mt-0.5 ${th} hover:underline text-left`} data-testid="drawer-last-login" title="Voir le détail de la dernière connexion">
              <LogIn size={10} className="inline mr-1" />Dernière connexion : <span className={row.last_login_at ? 'text-[#75D4D5] font-semibold' : ''}>{fdatetime(row.last_login_at)}</span>
            </button>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg flex-none ${light ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-white/10 text-white/60'}`} data-testid="drawer-close"><X size={17} /></button>
        </div>

        <div className="p-5">
          {canCancel && (
            <button onClick={cancelSub} disabled={canceling} data-testid="drawer-cancel-sub-btn"
              className="w-full flex items-center justify-center gap-2 mb-4 px-4 py-2.5 rounded-lg text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors disabled:opacity-40">
              <Ban size={13} /> {canceling ? 'Résiliation en cours…' : "Résilier l'abonnement"}
            </button>
          )}
          {row.cancel_at_period_end && ['active', 'trialing', 'past_due'].includes(row.sub_status) && (
            <div className="mb-4 rounded-lg px-3 py-2.5 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20" data-testid="drawer-cancel-pending">
              <p className="mb-2">Résiliation programmée — accès jusqu'au {row.current_period_end ? new Date(row.current_period_end).toLocaleDateString('fr-FR') : 'la fin de période'}.</p>
              <button onClick={resumeSub} disabled={canceling} data-testid="drawer-resume-sub-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors disabled:opacity-40">
                <RotateCcw size={13} /> {canceling ? 'Réactivation…' : "Réactiver l'abonnement"}
              </button>
            </div>
          )}
          {row.customer_id && (
            <button onClick={() => setShowRefund(true)} data-testid="drawer-refund-btn"
              className="w-full flex items-center justify-center gap-2 mb-4 px-4 py-2.5 rounded-lg text-xs font-bold bg-[#E9641A]/10 text-[#E9641A] border border-[#E9641A]/30 hover:bg-[#E9641A]/20 transition-colors">
              <Euro size={13} /> Rembourser (total ou partiel)
            </button>
          )}
          {row.has_account && (
            <div className={`rounded-xl border p-3 mb-4 ${light ? 'border-gray-200 bg-gray-50' : 'border-white/10 bg-white/[0.03]'}`} data-testid="drawer-access-control">
              <p className={`text-[11px] font-bold mb-2 ${th}`}>ACCÈS MAYA+ (manuel, indépendant de l'abonnement)</p>
              <div className="flex gap-1.5">
                {[['auto', 'Auto (abonnement)'], ['granted', 'Activé (offert)'], ['blocked', 'Bloqué']].map(([m, label]) => (
                  <button key={m} onClick={() => changeAccess(m)} disabled={accessBusy || accessMode === null} data-testid={`access-mode-${m}`}
                    className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${accessMode === m
                      ? (m === 'blocked' ? 'bg-red-500/15 text-red-400 border-red-500/40' : m === 'granted' ? 'bg-green-500/15 text-green-500 border-green-500/40' : 'bg-blue-500/15 text-blue-400 border-blue-500/40')
                      : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/45'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {row.has_account && (!['active', 'trialing'].includes(row.sub_status) || row.cancel_at_period_end) && (
            <button onClick={deleteAccount} data-testid="drawer-delete-account-btn"
              className="w-full flex items-center justify-center gap-2 mb-4 px-4 py-2.5 rounded-lg text-xs font-bold bg-red-600/10 text-red-400 border border-red-500/30 hover:bg-red-600/20 transition-colors">
              <Ban size={13} /> Supprimer définitivement ce compte {row.cancel_at_period_end && ['active', 'trialing'].includes(row.sub_status) ? '(résiliation immédiate incluse)' : 'résilié'}
            </button>
          )}
          <SubscriptionHistory email={row.email} light={light} />

          {!error && (
            <div className="mb-4">
              <button onClick={resetPassword} disabled={resetting} data-testid="drawer-reset-password-btn"
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold border transition-colors disabled:opacity-40 ${light ? 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100' : 'bg-white/5 text-white/80 border-white/15 hover:bg-white/10'}`}>
                <KeyRound size={13} /> {resetting ? 'Réinitialisation…' : 'Réinitialiser le mot de passe'}
              </button>
              {tempPass && (
                <div className="mt-2 rounded-lg px-3 py-2.5 text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 flex items-center justify-between" data-testid="drawer-temp-password">
                  <span>Mot de passe temporaire : <b className="font-mono text-sm">{tempPass}</b></span>
                  <button onClick={() => { navigator.clipboard.writeText(tempPass); toast.success('Copié'); }} data-testid="drawer-copy-temp-password"
                    className="p-1.5 rounded hover:bg-emerald-500/20 flex-none" title="Copier"><Copy size={13} /></button>
                </div>
              )}
            </div>
          )}

          {error && <div className={`rounded-lg px-3 py-3 text-xs ${light ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/10 text-amber-300'}`} data-testid="drawer-no-account">{error}</div>}
          {!error && !data && <p className={`text-xs py-8 text-center ${th}`}>Chargement…</p>}
          {data && (
            <>
              <div className="grid grid-cols-3 gap-2 mb-5">
                {[{ icon: Clock, v: fmtDur(data.totals.seconds), l: 'Temps visionné' },
                  { icon: PlayCircle, v: data.totals.sessions, l: 'Lectures' },
                  { icon: Film, v: data.totals.contents, l: 'Contenus' }].map(k => (
                  <div key={k.l} className={`rounded-lg border p-3 text-center ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/10'}`}>
                    <k.icon size={14} className="text-[#E9641A] mx-auto mb-1" />
                    <p className={`text-sm font-bold ${tx}`}>{k.v}</p>
                    <p className={`text-[9px] uppercase tracking-wide ${th}`}>{k.l}</p>
                  </div>
                ))}
              </div>

              {data.devices.length > 0 && (
                <div className="mb-4">
                  <p className={`text-[11px] font-bold uppercase tracking-wide mb-1.5 ${th}`}><Monitor size={11} className="inline mr-1" />Appareils</p>
                  <div className="flex flex-wrap gap-1.5">{data.devices.map(d => <span key={d} className={`px-2 py-1 rounded-full text-[10px] font-medium ${chip}`}>{d}</span>)}</div>
                </div>
              )}
              {data.locations.length > 0 && (
                <div className="mb-5">
                  <p className={`text-[11px] font-bold uppercase tracking-wide mb-1.5 ${th}`}><MapPin size={11} className="inline mr-1" />Localisations</p>
                  <div className="flex flex-wrap gap-1.5">{data.locations.map(l => <span key={l} className={`px-2 py-1 rounded-full text-[10px] font-medium ${chip}`}>{l}</span>)}</div>
                </div>
              )}

              <p className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${th}`}>Ce qu'il/elle regarde ({data.rows.length})</p>
              {data.rows.length === 0 && <p className={`text-xs ${th}`}>Aucun visionnage enregistré pour le moment.</p>}
              <div className="space-y-2" data-testid="drawer-watch-list">
                {data.rows.map(r => (
                  <div key={r.content_id} className={`rounded-lg border p-2.5 flex gap-2.5 ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/10'}`}>
                    {r.poster_url && <img src={r.poster_url} alt="" className="w-9 h-13 object-cover rounded flex-none" style={{ height: 52 }} />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold truncate ${tx}`}>{r.title}</p>
                      <p className={`text-[10px] capitalize ${th}`}>{r.type}{r.last ? ` · vu le ${fdatetime(r.last)}` : ''}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-[10px] font-bold text-[#E9641A]">{fmtDur(r.seconds)}</span>
                        <span className={`text-[10px] ${th}`}>{r.sessions} lecture(s)</span>
                        {typeof r.progress === 'number' && (
                          <div className="flex items-center gap-1 flex-1">
                            <div className={`h-1 flex-1 rounded-full overflow-hidden ${light ? 'bg-gray-200' : 'bg-white/10'}`}>
                              <div className="h-full bg-[#E9641A] rounded-full" style={{ width: `${Math.min(100, r.progress)}%` }} />
                            </div>
                            <span className={`text-[9px] ${th}`}>{Math.round(r.progress)}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {showLastSession && <LastSessionModal email={row.email} light={light} onClose={() => setShowLastSession(false)} />}
      {showRefund && <RefundModal row={row} light={light} onClose={() => setShowRefund(false)} />}
    </div>
  );
}
